/**
 * CRM ▸ Releases ▸ **one release** (`/crm/releases/10.2`).
 *
 * Everything on this page is about ONE release: its header, and the versions
 * shipped inside it. It is master–detail — a rail of versions down the left,
 * the selected version filling the right — because the reason to come here
 * is almost always "what is in the latest one?", and then "and the one
 * before it?", which a rail answers with one click and a stack of expanding
 * cards answered with a scroll.
 *
 * The newest version is selected on arrival. Which one is selected rides in
 * the URL (`?version=10.2.0.27`), so a refresh, the back button and a pasted
 * link all land on the same version. An unknown value falls back to the
 * newest; an unknown RELEASE in the path says so and offers the folders.
 *
 * The release is a container. There is no release-level change log,
 * documents or files: the downloads, the change log, the documentation and
 * the download journal all belong to the version on the right, and the
 * dialogs that write them (Manage files, Add document, the journal) open
 * from there. Curation of the container itself — New version, Edit, Publish,
 * Delete — is in the header.
 */
import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { PagePermissionGuard } from "@crm-src/components/page-permission-guard";

import { ApiError } from "../../../lib/api.js";
import { ReleaseArtifactsDialog } from "./ReleaseArtifactsDialog.js";
import { ReleaseDownloadJournalDialog } from "./ReleaseDownloadJournalDialog.js";
import { ReleaseHeader } from "./ReleaseHeader.js";
import { VersionDetail } from "./VersionDetail.js";
import { VersionRail } from "./VersionRail.js";
import { findFamily, versionsOf } from "./release-families.js";
import { RELEASES_PATH, VERSION_PARAM } from "./release-paths.js";
import type { ReleaseDTO } from "./releases-api.js";
import { useReleaseCuration } from "./useReleaseCuration.js";
import { useReleases } from "./useReleases.js";

export default function ReleaseDetailPage(): JSX.Element {
  return (
    <PagePermissionGuard requiredPermission="products_pricing:access">
      <ReleaseView />
    </PagePermissionGuard>
  );
}

function ReleaseView(): JSX.Element {
  const navigate = useNavigate();
  const { release: releaseParam } = useParams<{ release: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { families, isLoading, isError, error, refetch, invalidate } = useReleases();

  const family = findFamily(families, releaseParam ?? null);
  const versions = useMemo(() => (family ? versionsOf(family) : []), [family]);

  const selectVersion = useCallback(
    (version: string | null): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (version === null) next.delete(VERSION_PARAM);
          else next.set(VERSION_PARAM, version);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const curation = useReleaseCuration({
    families,
    invalidate,
    onDeleted: (gone) => {
      // Deleting the release itself leaves nothing to stand on. Deleting a
      // version drops the selection back to the newest one left.
      if (family && gone.id === family.line.id) navigate(RELEASES_PATH, { replace: true });
      else if (searchParams.get(VERSION_PARAM) === gone.version) selectVersion(null);
    },
  });

  // Which version is open: the URL's pick when it names one that exists,
  // else the newest. Never held in state — the URL is the state.
  const wanted = searchParams.get(VERSION_PARAM);
  const selected: ReleaseDTO | undefined =
    versions.find((v) => v.version === wanted) ?? versions[0];

  // A `?version=` that names nothing (a deleted version, a typo) is dropped
  // rather than left lying about what is shown.
  useEffect(() => {
    if (wanted && versions.length > 0 && !versions.some((v) => v.version === wanted)) {
      selectVersion(null);
    }
  }, [wanted, versions, selectVersion]);

  // Which version's files / journal dialog is open. Held as the whole row so
  // the dialog can title itself without a second lookup.
  const [filesFor, setFilesFor] = useState<ReleaseDTO | null>(null);
  const [journalFor, setJournalFor] = useState<ReleaseDTO | null>(null);

  if (isLoading) return <PageLoader />;

  if (isError) {
    return (
      <Box>
        <BackButton to={RELEASES_PATH} label="All releases" size="small" />
        <Box sx={{ mt: 2, borderRadius: 1, border: 1, borderColor: "error.main", p: 2, fontSize: 14 }}>
          <Typography sx={{ mb: 1, color: "error.main" }}>
            Could not load releases
            {error instanceof ApiError && error.message ? `: ${error.message}` : "."}
          </Typography>
          <Button variant="contained" size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!family) {
    return (
      <Box>
        <BackButton to={RELEASES_PATH} label="All releases" size="small" />
        <Typography sx={{ py: 6, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
          There is no release {releaseParam ?? ""}. It may have been deleted, or the link may be
          wrong.
        </Typography>
      </Box>
    );
  }

  const { line, hasLine } = family;

  return (
    <Box data-testid={`release-page-${line.version}`}>
      <BackButton to={RELEASES_PATH} label="All releases" size="small" />

      <Box sx={{ mt: 2 }}>
        <ReleaseHeader
          line={line}
          versionCount={versions.length}
          hasLine={hasLine}
          canEdit={curation.canEdit}
          onAddVersion={() => curation.openAddVersion(line)}
          onEdit={() => curation.openEdit(line)}
          onPublish={() => curation.publish(line)}
          onDelete={() => curation.requestDelete(line)}
        />
      </Box>

      {versions.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
          No versions in {line.version} yet.
          {curation.canEdit ? ` Use “New version” to add ${line.version}.0.1.` : ""}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            mt: 2.5,
            gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 260px) minmax(0, 1fr)" },
            alignItems: "start",
          }}
        >
          <VersionRail
            releaseVersion={line.version}
            versions={versions}
            selectedId={selected?.id}
            onSelect={(v) => selectVersion(v.version)}
          />
          {selected && (
            <VersionDetail
              key={selected.id}
              release={selected}
              canEdit={curation.canEdit}
              canAuthorDocs={curation.canAuthorDocs}
              onOpenJournal={() => setJournalFor(selected)}
              onManageFiles={() => setFilesFor(selected)}
              onEdit={() => curation.openEdit(selected)}
              onPublish={() => curation.publish(selected)}
              onDelete={() => curation.requestDelete(selected)}
            />
          )}
        </Box>
      )}

      {curation.dialogs}

      {filesFor && (
        <ReleaseArtifactsDialog
          releaseId={filesFor.id}
          version={filesFor.version}
          open
          onClose={() => setFilesFor(null)}
        />
      )}
      {journalFor && (
        <ReleaseDownloadJournalDialog release={journalFor} open onClose={() => setJournalFor(null)} />
      )}
    </Box>
  );
}
